import React, { useState } from "react"
import { Box, Divider, Stack, Typography } from "@mui/material"
import CustomDrawer from "src/components/CustomDrawer"
import { Message, SourceDoc } from "../km"
import remarkGfm from "remark-gfm"
import ReactMarkdown from "react-markdown"
import { ShowMoreButton } from "src/components/browser/buttons/ShowMoreButton"
import { SourceLinkButton } from "./SourceLinkButton"
import { useResponsive } from "src/utils/useResponsive"
import { DrawerHeader } from "./DrawerHeader"
import { CloseModalIconButton } from "src/components/CloseModalIconButton"
import { useCanModifyDataSource } from "src/utils/useCanModifyDataSource"

const METADATA_REGEX = new RegExp(/^<metadata>.*?<\/metadata>/, "gs")

interface Props {
  message: Message
  question: string
  setMessage: (message?: Message) => void
  selectedTaxonomyCode?: string
}

export const SourcesDrawer = ({ message, setMessage, question, selectedTaxonomyCode }: Props) => {
  const canModify = useCanModifyDataSource(selectedTaxonomyCode)
  const { isMobile } = useResponsive()
  const onClose = () => {
    setMessage(undefined)
  }

  const topSection = <DrawerHeader header="SOURCES FOR:" value={question} />

  return (
    <CustomDrawer
      open={!!message}
      onClose={onClose}
      topSectionColor={"primary"}
      topBar={
        <Stack direction={"row"} justifyContent={isMobile ? "space-between" : "flex-end"} alignItems="flex-start">
          {isMobile && topSection}
          <CloseModalIconButton onClose={onClose} data-testid="km-close-sources-button" />
        </Stack>
      }
      topSection={!isMobile && topSection}
      bottomSection={
        <Stack
          sx={{
            px: { xs: 2, md: 4, lg: 8 },
            py: { xs: 1, md: 2 }
          }}
        >
          {message.sourceDocs &&
            message.sourceDocs.length > 0 &&
            message.sourceDocs.map((s, index) => {
              return <SourceBox key={index} doc={s} canModify={canModify} />
            })}
        </Stack>
      }
    />
  )
}

function noMetadata(text: string) {
  return text.replaceAll(METADATA_REGEX, "")
}

const SourceBox = ({ doc, canModify }: { doc: SourceDoc; canModify: boolean }) => {
  const [open, setOpen] = useState(false)
  const { isMobile } = useResponsive()

  const commonHeaderSize = ({ children }: { children: React.ReactNode }) => (
    <Typography variant={"h3"} sx={{ fontWeight: "bold", fontSize: "1.2rem" }}>
      {children}
    </Typography>
  )

  return (
    <>
      <Box
        sx={{
          height: open ? "auto" : isMobile ? "120px" : "150px",
          overflowY: "hidden"
        }}
      >
        <ReactMarkdown
          linkTarget="_blank"
          remarkPlugins={[remarkGfm]}
          components={{
            p: ({ children }) => (
              <Typography
                variant={isMobile ? "body2" : "body1"}
                sx={{
                  fontSize: isMobile ? "0.75rem" : "0.875rem",
                  lineHeight: 1.5
                }}
              >
                {children}
              </Typography>
            ),
            h1: commonHeaderSize,
            h2: commonHeaderSize,
            h3: commonHeaderSize
          }}
        >
          {noMetadata(doc.page_content)}
        </ReactMarkdown>
      </Box>
      <Box>
        <Stack
          direction={"row"}
          justifyContent={"space-between"}
          sx={{
            pb: 1,
            pt: isMobile ? 1 : 2,
            gap: isMobile ? 1 : 2
          }}
        >
          {canModify && <SourceLinkButton doc={doc} />}
          <ShowMoreButton
            data-testid="km-show-more-button"
            opened={open}
            onShowMoreClick={() => setOpen((prev) => !prev)}
            size={isMobile ? "small" : "medium"}
          />
        </Stack>
      </Box>
      <Divider sx={{ my: 1 }} />
    </>
  )
}
